import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutlet } from "react-router";
import { useApp } from "../context";
import { AudioDonation, AudioDonationAttributes } from "../parse";
import { AdminAudioListItem } from "./AdminAudioListItem";

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(2),
  },
  headerButtons: {
    display: "flex",
    gap: theme.spacing(1),
    float: "right",
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
}));

type FilterType = "all" | "synthetic" | "real";

export const AdminAudioDatasetRoute = observer(
  function AdminAudioDatasetRoute() {
    const app = useApp();
    const classes = useStyles();
    const navigate = useNavigate();
    const outlet = useOutlet();
    const { t } = useTranslation();
    const [filter, setFilter] = useState<FilterType>("all");
    const [loading, setLoading] = useState<boolean>(false);

    const filteredDonations = app.admin.audioDonations.filter(
      (donation: AudioDonationAttributes) => {
        if (!donation.dataset) {
          return false;
        }
        switch (filter) {
          case "synthetic":
            return donation.synthetic;
          case "real":
            return !donation.synthetic;
          case "all":
          default:
            return true;
        }
      },
    );

    const handleRemoveFromDataset = async (id: string) => {
      await app.admin.removeFromDataset(id);
    };

    const handleDelete = (id: string) => {
      app.admin.delete(AudioDonation.className, id);
    };

    const handleDownloadDataset = () => {
      setLoading(true);
      app.admin.downloadDataset().then(() => {
        setLoading(false);
      });
    };

    if (outlet) {
      return outlet;
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" component="h2" className={classes.padding}>
            {t("admin-dataset-management")}
          </Typography>
        </Grid>

        <Grid item xs={12} className={classes.padding}>
          <Box className={classes.filterContainer}>
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 200 }}
            >
              <InputLabel>{t("filter")}</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterType)}
                label={t("filter")}
              >
                <MenuItem value="all">{t("all")}</MenuItem>
                <MenuItem value="synthetic">{t("synthetic")}</MenuItem>
                <MenuItem value="real">{t("real")}</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="textSecondary">
              {filteredDonations.length} {t("entries")}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            size="small"
            disabled={loading}
            color="primary"
            onClick={() => handleDownloadDataset()}
          >
            Download Dataset
          </Button>
          {loading && <CircularProgress />}
        </Grid>

        <Grid item xs={12}>
          <List>
            {filteredDonations.map((donation) => {
              const secondaryText = [
                `${t("language")}: ${donation.language}`,
                `${t("speaker-age")}: ${donation.speakerAge}`,
                `${t("voice-gender")}: ${donation.voiceGender}`,
                `${t("created")}: ${donation.createdAt.toLocaleDateString()}`,
                `${t("text")}: ${
                  donation.spokenText || t("no-text-available")
                }`,
              ].join(" | ");

              return (
                <>
                  <Divider variant="middle" component="li" />
                  <AdminAudioListItem
                    donation={donation}
                    secondaryText={secondaryText}
                    buttons={
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={() => handleRemoveFromDataset(donation.id)}
                        >
                          {t("button-remove-from-dataset")}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          color="default"
                          onClick={() => handleDelete(donation.id)}
                        >
                          {t("button-delete")}
                        </Button>
                      </>
                    }
                  />
                </>
              );
            })}
          </List>
        </Grid>
      </Grid>
    );
  },
);
